import type { ReactNode } from 'react';
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { logger } from '../util/logger';

interface ErrorInfo {
  message: string;
  error?: Error;
  stack?: string;
}

interface ErrorContextType {
  error: ErrorInfo | null;
  triggerError: (error: Error | string, stack?: string) => void;
  clearError: () => void;
}

const ErrorContext = createContext<ErrorContextType | undefined>(undefined);

// Global error callback that can be called from anywhere (including non-React code)
let globalTriggerError: ((error: Error | string, stack?: string) => void) | null =
  null;

/**
 * Display-only side channel for the redirect-mode error page's dev view — the
 * actual error-log payload is already built and sent by the caller (ErrorBoundary,
 * useGlobalErrorListeners) via `logError` before this runs. This only persists the
 * error to sessionStorage (so it survives the hard redirect) and updates the
 * currently-mounted ErrorProvider's state, if any.
 */
export const triggerGlobalError = (error: Error | string, stack?: string): void => {
  logger.log('🔴 triggerGlobalError called with:', {
    error: error instanceof Error ? error.message : error,
    hasStack: !!stack,
  });

  // Store error in sessionStorage for persistence across redirects
  const errorInfo = {
    message: error instanceof Error ? error.message : error,
    stack: stack ?? (error instanceof Error ? error.stack : undefined),
  };
  try {
    sessionStorage.setItem('globalError', JSON.stringify(errorInfo));
  } catch (error_) {
    // sessionStorage might not be available in some contexts
    logger.warn('Failed to store error in sessionStorage:', error_);
  }

  if (globalTriggerError) {
    globalTriggerError(error, stack);
  }
};

interface ErrorProviderProps {
  children: ReactNode;
}

export const ErrorProvider: React.FC<ErrorProviderProps> = ({ children }) => {
  // Check sessionStorage for persisted error on mount
  const [error, setError] = useState<ErrorInfo | null>(() => {
    try {
      const storedError = sessionStorage.getItem('globalError');
      if (storedError) {
        const parsed = JSON.parse(storedError) as ErrorInfo;
        // Clear from storage after reading
        sessionStorage.removeItem('globalError');
        return parsed;
      }
    } catch {
      // Ignore parsing errors
    }
    return null;
  });

  const triggerError = useCallback(
    (errorInput: Error | string, stack?: string) => {
      const errorInfo: ErrorInfo = {
        message:
          errorInput instanceof Error ? errorInput.message : errorInput,
        error: errorInput instanceof Error ? errorInput : undefined,
        stack: stack ?? (errorInput instanceof Error ? errorInput.stack : undefined),
      };
      setError(errorInfo);
    },
    []
  );

  const clearError = useCallback(() => {
    setError(null);
    // Also clear from sessionStorage
    try {
      sessionStorage.removeItem('globalError');
    } catch {
      // Ignore errors
    }
  }, []);

  // Register the global callback
  useEffect(() => {
    globalTriggerError = triggerError;
    return () => {
      globalTriggerError = null;
    };
  }, [triggerError]);

  const value = useMemo(
    () => ({
      error,
      triggerError,
      clearError,
    }),
    [error, triggerError, clearError]
  );

  return (
    <ErrorContext.Provider value={value}>{children}</ErrorContext.Provider>
  );
};

export const useError = (): ErrorContextType => {
  const context = useContext(ErrorContext);
  if (context === undefined) {
    throw new Error('useError must be used within an ErrorProvider');
  }
  return context;
};
